import { useState, useCallback } from "react";
import { useGetTaskQuery } from "../api";
import type { Task, TaskListItem, TaskStatus } from "../types";

interface UseBoardModalsProps {
  isSingleProject: boolean;
  projectId: number | null;
}

export function useBoardModals({ isSingleProject, projectId }: UseBoardModalsProps) {
  const [detailTask, setDetailTask] = useState<{ task: TaskListItem; projectId: number } | null>(null);
  const [taskFormState, setTaskFormState] = useState<{
    defaultStatus: TaskStatus | null;
    projectId: number;
    mode: "create" | "edit";
    task?: Task;
    forceKpi?: boolean;
  } | null>(null);

  const {
    data: selectedTaskData,
    isLoading: isTaskLoading,
    isFetching: isTaskFetching,
  } = useGetTaskQuery(
    { projectId: detailTask?.projectId ?? 0, taskId: detailTask?.task.id ?? 0 },
    { skip: !detailTask },
  );

  function handleTaskClick(task: TaskListItem, taskProjectId: number) {
    setDetailTask({ task, projectId: taskProjectId });
  }

  function handleCreateTask(status: TaskStatus | null = null, taskProjectId: number = 0) {
    const pid = isSingleProject && projectId ? projectId : taskProjectId;
    setTaskFormState({ defaultStatus: status, projectId: pid, mode: "create" });
  }

  function handleEditTask() {
    if (!selectedTaskData || !detailTask) return;
    setTaskFormState({
      defaultStatus: selectedTaskData.status,
      projectId: detailTask.projectId,
      mode: "edit",
      task: selectedTaskData,
      forceKpi: selectedTaskData.task_type === "kpi",
    });
    setDetailTask(null);
  }

  function closeDetailModal() {
    setDetailTask(null);
  }

  function closeTaskForm() {
    setTaskFormState(null);
  }

  const openTaskById = useCallback(function openTaskById(taskId: number, taskProjectId: number) {
    const stub: TaskListItem = {
      id: taskId,
      title: "",
      status: "todo",
      priority: "normal",
      task_type: "first_level_task",
      assigned_to: null,
      assigned_to_name: "",
      parent_task: null,
      position: 0,
      due_date: null,
      tags: [],
      is_favorite: false,
      created_at: "",
      comment_count: 0,
      subtask_count: 0,
      requires_fsm_report: false,
      fsm_submission_status: null,
      fsm_submission_id: null,
    };
    setDetailTask({ task: stub, projectId: taskProjectId });
  }, []);

  return {
    detailTask,
    taskFormState,
    selectedTaskData,
    isTaskLoading,
    isTaskFetching,
    handleTaskClick,
    handleCreateTask,
    handleEditTask,
    closeDetailModal,
    closeTaskForm,
    openTaskById,
  };
}
